import Button from '@/components/Button'
import { useSiteData } from '@/context/SiteDataProvider'
import { useRouter } from 'next/router'
import { useEffect, useLayoutEffect, useRef } from 'react'
import styles from './Banner.module.scss'

const DEFAULT_LINK_TEXT = 'Learn more'
const DEFAULT_LINK_URL = '/motivation-technologies'
const BANNER_HEIGHT_VAR = '--banner-height'

// Known banner min-heights — mirror $motech-header-height(-mobile) in
// Banner.module.scss and the mobile breakpoint in styles/_Variables.scss.
const BANNER_MIN_HEIGHT = 79
const BANNER_MIN_HEIGHT_MOBILE = 77
const MOBILE_BREAKPOINT = 767

// Seed --banner-height with the known min-height during SSR so the fixed header,
// mobile menu, and body offset are already correct on the first paint — before
// the client measures the real (possibly wrapped) height. Without this the
// header renders stacked on top of the banner and jumps down after hydration.
// Deterministic (constants only), so it never causes a hydration mismatch.
const SSR_HEIGHT_STYLE =
  `:root{${BANNER_HEIGHT_VAR}:${BANNER_MIN_HEIGHT}px}` +
  `@media (max-width:${MOBILE_BREAKPOINT}px){:root{${BANNER_HEIGHT_VAR}:${BANNER_MIN_HEIGHT_MOBILE}px}}`

// useLayoutEffect warns during SSR; fall back to useEffect on the server (where
// it is a no-op) and keep pre-paint measurement on the client.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

export default function Banner() {
  const router = useRouter()
  const { banner } = useSiteData()
  const ref = useRef(null)

  // Strip the query string so preview URLs (?preview=true) still match.
  const isHomepage = router.asPath.split('?')[0] === '/'
  const isVisible = isHomepage && Boolean(banner?.enabled) && Boolean(banner?.message)

  // CMS-editable link may point off-site; open external targets in a new tab
  // with rel hardening. Internal links keep default same-tab navigation.
  const isExternal = /^https?:\/\//i.test(banner?.linkUrl || '')

  // Publish the banner's measured height as a CSS var so the fixed header and
  // body offset below it. An inline style on documentElement overrides the SSR
  // <style> seed above. Resets to 0 (unset) everywhere the banner is not shown,
  // so non-homepage routes are unaffected.
  useIsomorphicLayoutEffect(() => {
    const root = document.documentElement
    const el = ref.current

    if (!isVisible || !el) {
      root.style.setProperty(BANNER_HEIGHT_VAR, '0px')
      return
    }

    const setHeight = () => root.style.setProperty(BANNER_HEIGHT_VAR, `${el.offsetHeight}px`)
    setHeight()

    // Prefer ResizeObserver to track responsive wrapping; fall back to a window
    // resize listener where it is unavailable (older/embedded browsers, some
    // test runners) so a missing global can never throw and break the page.
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(setHeight)
      observer.observe(el)
      return () => {
        observer.disconnect()
        root.style.setProperty(BANNER_HEIGHT_VAR, '0px')
      }
    }

    window.addEventListener('resize', setHeight)
    return () => {
      window.removeEventListener('resize', setHeight)
      root.style.setProperty(BANNER_HEIGHT_VAR, '0px')
    }
  }, [isVisible])

  if (!isVisible) {
    return null
  }

  return (
    <>
      <style>{SSR_HEIGHT_STYLE}</style>
      <aside ref={ref} className={styles.banner} aria-label="Site announcement" data-cy="site-banner">
        <div className={styles.inner}>
          <p className={styles.message} data-cy="site-banner-message">
            {banner.message}
          </p>
          <Button
            href={banner.linkUrl || DEFAULT_LINK_URL}
            className={styles.cta}
            {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {banner.linkText || DEFAULT_LINK_TEXT}
          </Button>
        </div>
      </aside>
    </>
  )
}
